<script setup lang="ts">
import type { SlideshowConfig } from "@/models"
import { useHead } from "@unhead/vue"
import { useRouter } from "vue-router"
import { useCreateSlideshowConfig, useGetEventVault } from "@/api"
import { useGetSlideshowVideoPlan } from "@/api/getSlideshowVideoPlan"
import { SlideshowForm } from "@/components"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useConvertedRouteParam, useTranslation } from "@/composables"
import { SidebarHostEventLayout } from "@/layouts"
import { TaggingBehavior } from "@/types"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const eventVaultId = useConvertedRouteParam("id", "/event/[id]/slideshow/create", value => Number(value))

const { t } = useTranslation()
const toast = useToast()

const router = useRouter()

const { data: eventVault, isLoading } = useGetEventVault(eventVaultId)
const { data: slideshowVideoPlan } = useGetSlideshowVideoPlan(eventVaultId)

const { mutate, isPending, isError } = useCreateSlideshowConfig(eventVaultId)

function onSlideshowCreate(slideshowConfig: SlideshowConfig) {
  if (!eventVaultId.value) {
    return
  }

  mutate(
    { slideshowConfig, eventVaultId: eventVaultId.value },
    {
      onSuccess: () => {
        toast.success({
          title: t("host_slideshow_create.success_title"),
          description: t("host_slideshow_create.success_content"),
          icon: "CircleCheck",
        })

        router.push(`/event/${eventVaultId.value}/slideshow`)
      },
      onError: () => toast.error(),
    },
  )
}

useHead({ title: t("host_slideshow_create.heading") })
</script>

<template>
  <SidebarHostEventLayout>
    <h1 class="font-heading font-bold text-2xl mb-8" data-test="slideshow-create-heading">
      {{ t("host_slideshow_create.heading") }}
    </h1>
    <SlideshowForm
      v-if="!isLoading" :is-loading="!isError && isPending"
      :albums-enabled="eventVault?.eventVaultSettings?.albumsEnabled"
      :tags-enabled="eventVault?.eventVaultSettings?.taggingBehavior === TaggingBehavior.On"
      :has-feature-image="Boolean(eventVault?.coverImageUrl)"
      :slideshow-videos-enabled="slideshowVideoPlan?.slideshowVideosEnabled ?? false"
      @submit="(slideshowConfig) => onSlideshowCreate(slideshowConfig)"
    />
  </SidebarHostEventLayout>
</template>
